import { Button, Modal } from '@/components/basic';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { LoadingSpinner } from '@/components/util/LoadingSpinner';
import { getAllResourcesIds } from '@gettactic/helpers/src/map/svg/getAllResourcesIds';
import { renameInteractiveZonesSvg } from '@gettactic/helpers/src/map/svg/renameInteractiveZonesSvg';
import { IResourceWithSchedules } from '@gettactic/hooks';
import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

type Props = {
  onClose: () => void;
  editGroupId: string | null;
  resources: IResourceWithSchedules[];
  svg: SVGSVGElement;
  setMapChanges: React.Dispatch<React.SetStateAction<number>>;
};

export function EditMapResourceModal({
  editGroupId,
  setMapChanges,
  resources,
  onClose,
  svg
}: Props) {
  const [loading, setLoading] = useState(false);
  const form = useForm({
    defaultValues: {
      resource: editGroupId
    }
  });

  const submitEdit = async ({ resource }: { resource: string | null }) => {
    setLoading(true);
    if (!editGroupId || !resource) {
      return;
    }
    if (resource === 'unassigned') {
      // for unassigned resources we need to
      // create a contrived not used reso id
      resource = `reso_unassigned_${Math.random()}`;
    }
    if (renameInteractiveZonesSvg(svg, editGroupId, resource)) {
      setMapChanges((x) => x + 1);
    }
    setLoading(false);
    onClose();
  };
  const usedIds = getAllResourcesIds(svg);
  const notAssigned = useMemo(
    () => ({ label: 'Not assigned', value: 'unassigned' }),
    []
  );
  const resourceList = useMemo(
    () =>
      resources?.reduce(
        (acc, x) => {
          if (isAlreadyUsed(x.id)) {
            return acc;
          }
          return acc.concat([
            {
              value: x.id,
              label: x.name
            }
          ]);
        },
        [] as { value: string; label: string }[]
      ),
    [resources, notAssigned]
  );

  const defaultValue =
    resourceList.find((x) => x.value === editGroupId) || notAssigned;
  const options = resourceList;

  function isAlreadyUsed(id: string): boolean {
    return usedIds.includes(id);
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(submitEdit)}>
        <div>
          <div className="col-span-12 mt-4 sm:col-span-6">
            <h3 className="mt-4 text-base font-bold text-gray-800">
              Resource Assignment
            </h3>

            <p className="mb-2 text-base text-gray-800">
              Assigning a resource makes it interactive. Marking something as
              "Not assigned" makes the item a static image i.e. you will not be
              able to interact with the element on the map.
            </p>
            <FormField
              control={form.control}
              rules={{ required: true }}
              name="resource"
              render={({ field }) => (
                <>
                  <FormItem>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={defaultValue.value}
                    >
                      <FormControl>
                        <SelectTrigger className="text-base">
                          <SelectValue placeholder="Assign a resource to this map object..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="z-[300] max-h-[300px] overflow-y-auto">
                        <OptionsCountLabel
                          count={options.length}
                          className="w-full pl-8 text-sm text-gray-500 border-b border-gray-200 pb-2 select-none cursor-default"
                        />
                        <SelectItem
                          value={notAssigned.value}
                          className="text-base hover:cursor-pointer hover:bg-gray-100"
                        >
                          {notAssigned.label}
                        </SelectItem>
                        {options.map((option, index) => (
                          <SelectItem
                            key={option.value}
                            value={option.value}
                            className="text-base hover:cursor-pointer hover:bg-gray-100"
                            itemIndex={index + 1}
                            totalCount={options.length}
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                </>
              )}
            />
          </div>
        </div>
        <Modal.Footer>
          <Button
            className="w-full sm:w-auto"
            layout="squared"
            variant="white"
            font="boldGray"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            className="mt-4 w-full sm:mt-0 sm:ml-3 sm:w-auto"
            layout="squared"
            variant="primary"
            type="submit"
            disabled={loading}
          >
            {loading ? (
              <LoadingSpinner color="text-white" />
            ) : (
              <span>Save Changes</span>
            )}
          </Button>
        </Modal.Footer>
      </form>
    </Form>
  );
}

const OptionsCountLabel = ({
  count,
  className
}: {
  count: number;
  className?: string;
}) => {
  if (count > 0) {
    return (
      <div className={className}>
        {count} Resource{count > 1 ? 's' : ''} Unassigned
      </div>
    );
  }

  return <div className={className}>All resources are already assigned.</div>;
};
